import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { INSTAGRAM_URL } from "@/components/Header";

const INQUIRY_TYPES = [
  {
    key: "Guest Inquiry",
    desc: "Artists, entertainers, athletes, and personalities who want to pull up a chair poolside.",
    extra: { name: "detail", label: "Links / social profiles" },
  },
  {
    key: "Event Booking",
    desc: "Private events & celebrations at the Café — available Saturdays & Sundays after 4 PM.",
    extra: { name: "detail", label: "Event date / party size" },
  },
  {
    key: "Chef / Cooking Segment",
    desc: "Chefs and culinary talent interested in leading a cooking segment on the show.",
    extra: { name: "detail", label: "Culinary background / restaurant" },
  },
  {
    key: "Partnerships & Sponsorships",
    desc: "Brands and partners who want to build something with Poolside Cooking.",
    extra: { name: "detail", label: "Company / brand" },
  },
  {
    key: "Media / Press",
    desc: "Interviews, appearances, press assets, and media requests.",
    extra: { name: "detail", label: "Outlet / deadline" },
  },
  {
    key: "General Inquiry",
    desc: "Everything else — we'd love to hear from you.",
    extra: null,
  },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initial = INQUIRY_TYPES.some((t) => t.key === params.get("type")) ? params.get("type") : INQUIRY_TYPES[0].key;
  const [type, setType] = useState(initial);
  const [form, setForm] = useState({ name: "", email: "", detail: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const active = INQUIRY_TYPES.find((t) => t.key === type);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries", {
        inquiry_type: type,
        name: form.name,
        email: form.email,
        detail: form.detail,
        message: form.message,
      });
      setSent(true);
      toast.success("Thanks — the Poolside Cooking team will be in touch.");
      setForm({ name: "", email: "", detail: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  const inputCls =
    "w-full border border-slate-300 bg-white px-4 py-3.5 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-pool transition-colors";

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Pull up a chair."
        sub="Guests, events, partnerships, press — pick a lane and tell us about yourself."
      />
      <section className="py-14 sm:py-16 bg-white">
        <div className="container-site grid lg:grid-cols-5 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-2">
            <p className="label-eyebrow mb-6">I'm reaching out about</p>
            <div className="flex flex-col gap-3" data-testid="inquiry-type-list">
              {INQUIRY_TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  data-testid={`inquiry-type-${t.key.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`text-left border rounded-xl p-5 transition-colors ${
                    type === t.key ? "border-pool bg-pool-light/40" : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="font-bold text-sm text-slate-900">{t.key}</span>
                    {type === t.key && <Check className="h-4 w-4 text-pool shrink-0" />}
                  </span>
                  <span className="block mt-1.5 text-xs text-slate-500 leading-relaxed">{t.desc}</span>
                </button>
              ))}
            </div>
            <p className="mt-8 text-sm text-slate-500">
              Prefer social? DM us on{" "}
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="text-pool font-semibold hover:underline" data-testid="contact-instagram">
                Instagram
              </a>
              .
            </p>
          </Reveal>

          <Reveal className="lg:col-span-3" delay={0.1}>
            {sent ? (
              <div className="border border-slate-200 rounded-2xl p-10 sm:p-14 text-center" data-testid="contact-success">
                <span className="mx-auto h-14 w-14 rounded-full bg-pool flex items-center justify-center text-white">
                  <Check className="h-6 w-6" />
                </span>
                <h2 className="mt-8 font-display font-black text-3xl text-slate-900">Message received.</h2>
                <p className="mt-4 text-slate-600 text-sm leading-relaxed max-w-md mx-auto">
                  Thanks for reaching out about a {type.toLowerCase()}. The Poolside Cooking team reviews every
                  inquiry and will be in touch.
                </p>
                <button onClick={() => setSent(false)} className="link-underline mt-8" data-testid="contact-send-another">
                  Send another message <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-slate-200 rounded-2xl p-8 sm:p-10" data-testid="contact-form">
                <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-pool mb-8">{type}</p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Name *</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inputCls}
                      placeholder="Your full name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Email *</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputCls}
                      placeholder="you@example.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                {active.extra && (
                  <div className="mt-5">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      {active.extra.label}
                    </label>
                    <input
                      value={form.detail}
                      onChange={(e) => setForm({ ...form, detail: e.target.value })}
                      className={inputCls}
                      data-testid="contact-detail-input"
                    />
                  </div>
                )}
                <div className="mt-5">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Message *</label>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className={inputCls}
                    placeholder="Tell us about yourself and why you'd be a fit for the show…"
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="mt-8 inline-flex items-center gap-3 bg-ink hover:bg-pool disabled:opacity-60 text-white text-xs tracking-[0.15em] uppercase font-bold px-8 py-4 rounded-full transition-colors"
                  data-testid="contact-submit-button"
                >
                  {busy ? "Sending…" : "Send inquiry"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
